import { extend, Application as PixiReactApplication } from "@pixi/react";
import type { FederatedPointerEvent } from "pixi.js";
import {
    BitmapText,
    Container,
    Graphics,
    PerspectiveMesh,
    type Application as PixiApplication,
    Point,
    Sprite,
    Text,
} from "pixi.js";
import type { ReactNode, Ref } from "react";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { CustomViewport, MIN_WORLD_VISIBLE_SPAN } from "./CustomViewport";
import { SceneProvider } from "./context/SceneContext";
import type { WorldExtent } from "./geometry/worldExtent";
import { combineWorldExtents } from "./geometry/worldExtent";
import { getMapPointerPosition } from "./getMapPointerPosition";
import { useScene } from "./hooks/useScene";
import { MapCoordinates } from "./MapCoordinates";
import type { InteractionTarget } from "./managers/InteractionManager";
import type {
    CorvusMapHandle,
    FitBoundsOptions,
    MapTapEvent,
    WorldRect,
} from "./types";

const DEFAULT_FIT_WORLD_PADDING = 5;
const DEFAULT_MIN_WORLD_VISIBLE_SPAN = 10;

function nonNegativeOrDefault(
    value: number | undefined,
    fallback: number,
): number {
    return value !== undefined && Number.isFinite(value) && value >= 0
        ? value
        : fallback;
}

extend({
    Graphics,
    Container,
    Text,
    BitmapText,
    Sprite,
    PerspectiveMesh,
    CustomViewport,
});

export interface CorvusMapProps {
    worldRect: WorldRect | null;
    /** Called once for each newly usable viewport after the imperative map controls are installed. */
    onReady?: () => void;
    /** Called when a non-navigation tap reaches the viewport without being handled by a child. */
    onUnhandledClick?: (event: MapTapEvent) => void;
    disableInteractions?: boolean;
    children?: ReactNode;
}

function ViewportInteractionTarget({
    onUnhandledClick,
}: Pick<CorvusMapProps, "onUnhandledClick">) {
    const { coordinates, interactionManager, viewport } = useScene();
    const target = useRef<InteractionTarget>({}).current;

    const handlePointerOver = useCallback(
        (event: FederatedPointerEvent) => {
            interactionManager.pointerOver(event, target);
        },
        [interactionManager, target],
    );

    const handlePointerOut = useCallback(
        (event: FederatedPointerEvent) => {
            interactionManager.pointerOut(event, target);
        },
        [interactionManager, target],
    );

    const handlePointerTap = useCallback(
        (event: FederatedPointerEvent) => {
            const pointerPosition = getMapPointerPosition(
                event,
                viewport,
                coordinates,
            );
            if (!pointerPosition) {
                target.onClick = undefined;
                interactionManager.pointerTap(event, target, {
                    stopPropagation: false,
                });
                return;
            }

            const mapEvent: MapTapEvent = pointerPosition;

            target.onClick = onUnhandledClick
                ? () => onUnhandledClick(mapEvent)
                : undefined;
            interactionManager.pointerTap(event, target, {
                stopPropagation: false,
            });
        },
        [coordinates, interactionManager, onUnhandledClick, target, viewport],
    );

    useEffect(() => {
        // Capture establishes a fallback semantic target before a child can replace it.
        viewport.addEventListener("pointerover", handlePointerOver, true);
        viewport.addEventListener("pointerout", handlePointerOut, true);
        // Bubble observes only taps that a child semantic target did not consume.
        viewport.addEventListener("pointertap", handlePointerTap);
        return () => {
            viewport.removeEventListener(
                "pointerover",
                handlePointerOver,
                true,
            );
            viewport.removeEventListener("pointerout", handlePointerOut, true);
            viewport.removeEventListener("pointertap", handlePointerTap);
            interactionManager.removeTarget(target);
        };
    }, [
        handlePointerOut,
        handlePointerOver,
        handlePointerTap,
        interactionManager,
        target,
        viewport,
    ]);

    return null;
}

const isFiniteWorldRect = (
    worldRect: WorldRect | null,
): worldRect is WorldRect => {
    return Boolean(
        worldRect &&
            Number.isFinite(worldRect.x) &&
            Number.isFinite(worldRect.y) &&
            Number.isFinite(worldRect.width) &&
            Number.isFinite(worldRect.height) &&
            worldRect.width > 0 &&
            worldRect.height > 0,
    );
};

const CorvusMap = forwardRef(function CorvusMap(
    {
        worldRect,
        onReady,
        onUnhandledClick,
        disableInteractions = false,
        children,
    }: CorvusMapProps,
    ref: Ref<CorvusMapHandle>,
) {
    const isValidWorldRect = isFiniteWorldRect(worldRect);
    const validWorldRect = isValidWorldRect ? worldRect : null;
    const coordinates = useMemo(
        () => (validWorldRect ? new MapCoordinates(validWorldRect) : null),
        [validWorldRect],
    );
    const sceneRect = useMemo(
        () =>
            coordinates && validWorldRect
                ? coordinates.worldRectToScene(validWorldRect)
                : null,
        [coordinates, validWorldRect],
    );

    const [app, setApp] = useState<PixiApplication | null>(null);
    const [viewport, setViewport] = useState<CustomViewport | null>(null);
    const [readyViewport, setReadyViewport] = useState<CustomViewport | null>(
        null,
    );
    const containerRef = useRef<HTMLDivElement | null>(null);
    const previousContainerRectRef = useRef<{
        left: number;
        top: number;
    } | null>(null);
    const notifiedReadyViewportRef = useRef<CustomViewport | null>(null);

    const viewportKey = validWorldRect
        ? `${validWorldRect.x}-${validWorldRect.y}-${validWorldRect.width}-${validWorldRect.height}`
        : "no-world";

    const handleViewportRef = useCallback(
        (nextViewport: CustomViewport | null) => {
            setViewport(nextViewport);
        },
        [],
    );

    useEffect(() => {
        if (!app || !viewport || !sceneRect || !containerRef.current) return;

        let active = true;

        const handleResize = () => {
            if (!active || !viewport.isUsable) return;

            const container = containerRef.current;
            if (!container) return;

            const previousRect = previousContainerRectRef.current;
            const { left, top } = container.getBoundingClientRect();

            app.resize();

            // app.resize() may synchronously cause lifecycle changes too,
            // so guard again before touching the viewport.
            if (!active || !viewport.isUsable) return;

            const width = app.renderer.width;
            const height = app.renderer.height;

            if (width === 0 || height === 0) return;

            viewport.resize(width, height, sceneRect.width, sceneRect.height);

            if (previousRect) {
                viewport.x -= left - previousRect.left;
                viewport.y -= top - previousRect.top;
            }

            previousContainerRectRef.current = { left, top };

            viewport.emit("resized");
            setReadyViewport(viewport);
        };

        handleResize();

        const observer = new ResizeObserver(handleResize);
        observer.observe(containerRef.current);

        return () => {
            active = false;
            observer.disconnect();
            previousContainerRectRef.current = null;
        };
    }, [app, viewport, sceneRect]);

    useEffect(() => {
        if (!app) return;
        app.stage.interactiveChildren = !disableInteractions;
    }, [app, disableInteractions]);

    const performFit = useCallback(
        (worldRect: WorldRect) => {
            if (!viewport?.isUsable || !coordinates) return;

            const targetSceneRect = coordinates.worldRectToScene(worldRect);
            if (
                !Number.isFinite(targetSceneRect.width) ||
                !Number.isFinite(targetSceneRect.height) ||
                targetSceneRect.width <= 0 ||
                targetSceneRect.height <= 0
            ) {
                return;
            }

            const fitScale = viewport.findFit(
                targetSceneRect.width,
                targetSceneRect.height,
            );
            const centerCoords = coordinates.worldToScene({
                x: worldRect.x + worldRect.width / 2,
                y: worldRect.y + worldRect.height / 2,
            });

            viewport.animate({
                time: 100,
                position: new Point(centerCoords.x, centerCoords.y),
                scale: fitScale,
                ease: "easeOutBounce",
            });
        },
        [viewport, coordinates],
    );

    const fitBounds = useCallback(
        (
            extents: WorldExtent | readonly WorldExtent[],
            options?: FitBoundsOptions,
        ) => {
            const worldExtents = Array.isArray(extents) ? extents : [extents];
            let combinedExtent: WorldRect | null;

            try {
                combinedExtent = combineWorldExtents(worldExtents);
            } catch {
                return;
            }

            if (!combinedExtent) return;

            const worldPadding = nonNegativeOrDefault(
                options?.worldPadding,
                DEFAULT_FIT_WORLD_PADDING,
            );

            const minWorldVisibleSpan = nonNegativeOrDefault(
                options?.minWorldVisibleSpan,
                DEFAULT_MIN_WORLD_VISIBLE_SPAN,
            );

            const paddedWidth = combinedExtent.width + 2 * worldPadding;
            const paddedHeight = combinedExtent.height + 2 * worldPadding;
            let width = Math.max(paddedWidth, MIN_WORLD_VISIBLE_SPAN);
            let height = Math.max(paddedHeight, MIN_WORLD_VISIBLE_SPAN);

            const largestSpan = Math.max(width, height);

            if (largestSpan < minWorldVisibleSpan) {
                const scale = minWorldVisibleSpan / largestSpan;
                width *= scale;
                height *= scale;
            }

            const x = combinedExtent.x + combinedExtent.width / 2 - width / 2;
            const y = combinedExtent.y + combinedExtent.height / 2 - height / 2;

            if (![x, y, width, height].every(Number.isFinite)) return;

            performFit({ x, y, width, height });
        },
        [performFit],
    );

    const fit = useCallback(() => {
        if (!validWorldRect) return;

        fitBounds(validWorldRect, {
            worldPadding:
                Math.max(validWorldRect.width, validWorldRect.height) * 0.05,
        });
    }, [fitBounds, validWorldRect]);

    useImperativeHandle(
        ref,
        () => ({
            zoomIn: () => {
                if (!viewport?.isUsable) return;
                viewport.animate({ time: 100, scale: viewport.scale.x * 1.5 });
            },
            zoomOut: () => {
                if (!viewport?.isUsable) return;
                viewport.animate({ time: 100, scale: viewport.scale.x / 1.5 });
            },
            fit,
            fitBounds,
        }),
        [viewport, fit, fitBounds],
    );

    useEffect(() => {
        if (viewport && readyViewport === viewport) fit();
    }, [viewport, readyViewport, fit]);

    useEffect(() => {
        if (
            !viewport ||
            readyViewport !== viewport ||
            !coordinates ||
            !validWorldRect ||
            !onReady ||
            notifiedReadyViewportRef.current === viewport
        ) {
            return;
        }

        notifiedReadyViewportRef.current = viewport;
        onReady();
    }, [viewport, readyViewport, coordinates, validWorldRect, onReady]);

    return (
        <div ref={containerRef} style={{ width: "100%", height: "100%" }}>
            <PixiReactApplication
                onInit={setApp}
                resizeTo={containerRef}
                backgroundColor={0xeeeeee}
                antialias
                resolution={window.devicePixelRatio}
                autoDensity
            >
                {app && validWorldRect && coordinates && sceneRect && (
                    <pixiCustomViewport
                        key={viewportKey}
                        ref={handleViewportRef}
                        events={app.renderer.events}
                        screenWidth={app.renderer.width}
                        screenHeight={app.renderer.height}
                        worldWidth={sceneRect.width}
                        worldHeight={sceneRect.height}
                        coordinates={coordinates}
                        sceneRect={sceneRect}
                        passiveWheel={false}
                    >
                        {viewport && (
                            <SceneProvider
                                viewport={viewport}
                                coordinates={coordinates}
                            >
                                <ViewportInteractionTarget
                                    onUnhandledClick={onUnhandledClick}
                                />
                                <pixiContainer
                                    label="map-root"
                                    sortableChildren
                                >
                                    <pixiContainer label="background-layer">
                                        <pixiGraphics
                                            draw={(graphics) => {
                                                graphics.clear();
                                                graphics.rect(
                                                    sceneRect.x,
                                                    sceneRect.y,
                                                    sceneRect.width,
                                                    sceneRect.height,
                                                );
                                                graphics.stroke({
                                                    color: 0xbbbbbb,
                                                    width: 0.5,
                                                });
                                            }}
                                        />
                                    </pixiContainer>
                                    {children}
                                </pixiContainer>
                            </SceneProvider>
                        )}
                    </pixiCustomViewport>
                )}
            </PixiReactApplication>
        </div>
    );
});

export default CorvusMap;
